import React, { useEffect, useState } from 'react'
import { FlatList, RefreshControl, Image, useWindowDimensions, TextInput, StyleSheet, TouchableOpacity, Linking } from 'react-native'
import { ArrowLeft, MapPin, Store, ChevronDown, ChevronUp, Phone, Globe, Clock, SlidersHorizontal, X, ParkingCircle, Accessibility, Wifi, Shirt, Coffee, CreditCard, Banknote, Smartphone, Share2, type LucideIcon } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import { Chip, ChipText, EmptyState, ProductCard, Spinner, StatusBadge, Text, TopBar, XStack, YStack, Sheet } from '@fliisi/pollen'
import theme from '../theme'

const GRID_PADDING = 16
const CARD_GAP = 12
const MIN_CARD_WIDTH = 150

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'StoreBrowse'>
  route: RouteProp<RootStackParamList, 'StoreBrowse'>
}

interface Product {
  id: string
  title: string
  title_en: string | null
  title_fi: string | null
  price: number
  status: string
  product_images: { id: string; image_url: string }[]
}

interface MarketInfo {
  id: string
  name: string
  store_type: string
  address_street: string | null
  address_city: string | null
  cover_image_url: string | null
  description: string | null
  phone: string | null
  email: string | null
  website_url: string | null
  opening_hours: Record<string, { open: string; close: string; closed: boolean }> | null
  settings: { amenities?: string[] } | null
}

const AMENITIES: { key: string; icon: LucideIcon; labelKey: string }[] = [
  { key: 'parking', icon: ParkingCircle, labelKey: 'parking' },
  { key: 'wheelchair', icon: Accessibility, labelKey: 'wheelchair_accessible' },
  { key: 'wifi', icon: Wifi, labelKey: 'free_wifi' },
  { key: 'fitting_room', icon: Shirt, labelKey: 'fitting_room' },
  { key: 'cafe', icon: Coffee, labelKey: 'cafe' },
  { key: 'card_payment', icon: CreditCard, labelKey: 'card_payment' },
  { key: 'cash_payment', icon: Banknote, labelKey: 'cash_payment' },
  { key: 'mobilepay', icon: Smartphone, labelKey: 'mobilepay' },
]

const STORE_TYPE_LABELS: Record<string, { en: string; fi: string }> = {
  flea_market: { en: 'Flea Market', fi: 'Kirpputori' },
  vintage_store: { en: 'Vintage Store', fi: 'Vintagekauppa' },
  thrift_store: { en: 'Thrift Store', fi: 'Säästökauppa' },
  antique_shop: { en: 'Antique Shop', fi: 'Antiikkiliike' },
  consignment_store: { en: 'Consignment', fi: 'Myyntitili' },
  retail_store: { en: 'Retail Store', fi: 'Vähittäiskauppa' },
}

const DAY_LABELS: Record<string, { en: string; fi: string }> = {
  monday: { en: 'Mon', fi: 'Ma' },
  tuesday: { en: 'Tue', fi: 'Ti' },
  wednesday: { en: 'Wed', fi: 'Ke' },
  thursday: { en: 'Thu', fi: 'To' },
  friday: { en: 'Fri', fi: 'Pe' },
  saturday: { en: 'Sat', fi: 'La' },
  sunday: { en: 'Sun', fi: 'Su' },
}
const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

export default function StoreBrowseScreen({ navigation, route }: Props) {
  const { marketId, marketName } = route.params
  const { t, language } = useLanguage()
  const { width } = useWindowDimensions()

  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [market, setMarket] = useState<MarketInfo | null>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [showDetails, setShowDetails] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'newest' | 'price_low' | 'price_high'>('newest')
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')

  const availableWidth = width - (GRID_PADDING * 2)
  const columns = Math.max(2, Math.floor(availableWidth / (MIN_CARD_WIDTH + CARD_GAP)))
  const cardWidth = (availableWidth - (CARD_GAP * (columns - 1))) / columns

  useEffect(() => { fetchData() }, [marketId, sortBy])

  const shareStore = async () => {
    const url = `${typeof window !== 'undefined' ? window.location.origin : 'https://fliisi.fi'}/store/${marketId}`
    const title = market?.name || marketName
    if (typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ title, url }) } catch { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(url) } catch { Linking.openURL(url) }
    }
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const { data: marketData } = await supabase
        .from('flea_markets')
        .select('id, name, store_type, address_street, address_city, cover_image_url, description, phone, email, website_url, opening_hours, settings')
        .eq('id', marketId)
        .single()
      setMarket(marketData)

      let query = supabase
        .from('products')
        .select('id, title, title_en, title_fi, price, status, created_at, product_images(id, image_url)')
        .eq('flea_market_id', marketId)
        .eq('status', 'for_sale')

      switch (sortBy) {
        case 'price_low': query = query.order('price', { ascending: true }); break
        case 'price_high': query = query.order('price', { ascending: false }); break
        default: query = query.order('created_at', { ascending: false })
      }

      const { data } = await query
      setProducts(data || [])
    } catch (e) {
      console.error('Error fetching store data:', e)
    } finally {
      setLoading(false)
    }
  }

  const getTitle = (p: Product) =>
    language === 'fi' && p.title_fi ? p.title_fi
    : language === 'en' && p.title_en ? p.title_en
    : p.title

  const getStoreTypeLabel = (type: string) => {
    const l = STORE_TYPE_LABELS[type]
    return l ? (language === 'fi' ? l.fi : l.en) : type
  }

  const filteredProducts = products.filter(p => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      const title = getTitle(p).toLowerCase()
      if (!title.includes(q)) return false
    }
    if (minPrice && p.price < parseFloat(minPrice)) return false
    if (maxPrice && p.price > parseFloat(maxPrice)) return false
    return true
  })

  const activeFilterCount = [minPrice, maxPrice].filter(Boolean).length + (sortBy !== 'newest' ? 1 : 0)

  const resetFilters = () => {
    setMinPrice('')
    setMaxPrice('')
    setSortBy('newest')
  }

  const renderStoreHeader = () => (
    <YStack bg="$backgroundCard" mb="$md">
      {/* Cover image or placeholder */}
      {market?.cover_image_url ? (
        <Image source={{ uri: market.cover_image_url }} style={{ width: '100%', height: 160 }} resizeMode="cover" />
      ) : (
        <YStack w="100%" h={120} bg="$backgroundSecondary" jc="center" ai="center">
          <Store size={48} color="#858B80" />
        </YStack>
      )}

      {/* Name + details toggle */}
      <YStack px="$md" pt="$md" pb="$sm">
        <XStack ai="center" jc="space-between">
          <Text preset="heading" fontSize={22} f={1}>{market?.name || marketName}</Text>
          <TouchableOpacity
            style={styles.detailsButton}
            onPress={() => setShowDetails(v => !v)}
          >
            <Text style={styles.detailsButtonText}>
              {showDetails ? t('hide_details') : t('store_info')}
            </Text>
            {showDetails
              ? <ChevronUp size={14} color={theme.colors.brand.primary} />
              : <ChevronDown size={14} color={theme.colors.brand.primary} />
            }
          </TouchableOpacity>
        </XStack>
      </YStack>

      {/* Expandable store profile */}
      {showDetails && (
        <YStack px="$md" pb="$md" gap="$sm" borderTopWidth={1} borderTopColor="$borderLight" pt="$sm">
          {market?.store_type && (
            <StatusBadge label={getStoreTypeLabel(market.store_type)} variant="info" alignSelf="flex-start" />
          )}
          {(market?.address_street || market?.address_city) && (
            <XStack ai="flex-start" gap={8}>
              <MapPin size={15} color="#858B80" style={{ marginTop: 2 }} />
              <Text preset="bodySmall" tone="secondary" f={1}>
                {[market?.address_street, market?.address_city].filter(Boolean).join(', ')}
              </Text>
            </XStack>
          )}
          {market?.description ? (
            <Text preset="bodySmall" tone="secondary">{market.description}</Text>
          ) : null}
          {market?.phone ? (
            <XStack ai="center" gap={8} onPress={() => Linking.openURL(`tel:${market.phone}`)} cursor="pointer" pressStyle={{ opacity: 0.7 }}>
              <Phone size={15} color="#748C70" />
              <Text preset="bodySmall" color="#748C70" textDecorationLine="underline">{market.phone}</Text>
            </XStack>
          ) : null}
          {market?.website_url ? (
            <XStack ai="center" gap={8} onPress={() => Linking.openURL(market.website_url!.startsWith('http') ? market.website_url! : `https://${market.website_url}`)} cursor="pointer" pressStyle={{ opacity: 0.7 }}>
              <Globe size={15} color="#748C70" />
              <Text preset="bodySmall" color="#748C70" textDecorationLine="underline">{market.website_url}</Text>
            </XStack>
          ) : null}
          {market?.opening_hours && (
            <YStack gap={4}>
              <XStack ai="center" gap={8} mb={2}>
                <Clock size={15} color="#858B80" />
                <Text preset="bodySmall" color="$textSecondary" fontWeight="600">
                  {t('opening_hours')}
                </Text>
              </XStack>
              {DAY_ORDER.map(day => {
                const hours = market.opening_hours?.[day]
                if (!hours) return null
                const dayLabel = DAY_LABELS[day]?.[language as 'en' | 'fi'] ?? day
                return (
                  <XStack key={day} jc="space-between" px={23}>
                    <Text preset="bodySmall" tone="secondary" w={30}>{dayLabel}</Text>
                    <Text preset="bodySmall" tone="secondary">
                      {hours.closed ? t('closed') : `${hours.open} – ${hours.close}`}
                    </Text>
                  </XStack>
                )
              })}
            </YStack>
          )}
          {(() => {
            const activeAmenities = AMENITIES.filter(a => market?.settings?.amenities?.includes(a.key))
            if (!activeAmenities.length) return null
            return (
              <XStack flexWrap="wrap" gap="$sm" mt={2}>
                {activeAmenities.map(amenity => {
                  const IconComponent = amenity.icon
                  return (
                    <XStack key={amenity.key} ai="center" gap={5} px={10} py={5} bg="$backgroundSecondary" br="$full">
                      <IconComponent size={13} color="#748C70" />
                      <Text preset="bodySmall" tone="secondary">{t(amenity.labelKey as any)}</Text>
                    </XStack>
                  )
                })}
              </XStack>
            )
          })()}
        </YStack>
      )}

      {/* Search bar + filter button in one row */}
      <XStack px="$md" pb="$sm" gap="$sm" ai="center">
        <XStack
          f={1}
          ai="center"
          bg="$backgroundSecondary"
          br="$md"
          borderWidth={1}
          borderColor="$border"
          px="$sm"
          gap="$xs"
        >
          <TextInput
            style={styles.searchInput}
            placeholder={t('search_products')}
            placeholderTextColor="#999"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery ? (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <X size={16} color="#999" />
            </TouchableOpacity>
          ) : null}
        </XStack>
        <TouchableOpacity style={[styles.filterIconButton, activeFilterCount > 0 && styles.filterIconButtonActive]} onPress={() => setShowFilters(true)}>
          <SlidersHorizontal size={18} color={activeFilterCount > 0 ? '#fff' : '#858B80'} />
          {activeFilterCount > 0 && (
            <YStack pos="absolute" t={-4} r={-4} w={16} h={16} br={8} bg="$primary" ai="center" jc="center">
              <Text fontSize={10} color="white" fontWeight="700">{activeFilterCount}</Text>
            </YStack>
          )}
        </TouchableOpacity>
      </XStack>

      {/* Product count */}
      <Text preset="bodySmall" tone="secondary" px="$md" pb="$sm">
        {filteredProducts.length} {filteredProducts.length === 1 ? t('product') : t('products')}
      </Text>
    </YStack>
  )

  const renderProduct = ({ item }: { item: Product }) => (
    <ProductCard
      w={cardWidth}
      imageUri={item.product_images?.[0]?.image_url}
      title={getTitle(item)}
      price={`€${item.price?.toFixed(2) || '0.00'}`}
      onPress={() => navigation.navigate('ProductView', { productId: item.id })}
    />
  )

  if (loading && !refreshing) {
    return (
      <YStack f={1} bg="$background">
        <TopBar
          title=""
          left={<YStack w={40} h={40} br="$lg" bg="$backgroundCard" jc="center" ai="center" onPress={() => navigation.goBack()} pressStyle={{ opacity: 0.7 }} animation="quick" cursor="pointer"><ArrowLeft size={20} color="#748C70" /></YStack>}
        />
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      </YStack>
    )
  }

  return (
    <YStack f={1} bg="$background">
      <TopBar
        title=""
        left={<YStack w={40} h={40} br="$lg" bg="$backgroundCard" jc="center" ai="center" onPress={() => navigation.goBack()} pressStyle={{ opacity: 0.7 }} animation="quick" cursor="pointer"><ArrowLeft size={20} color="#748C70" /></YStack>}
        right={<YStack w={40} h={40} br="$lg" bg="$backgroundCard" jc="center" ai="center" onPress={shareStore} pressStyle={{ opacity: 0.7 }} animation="quick" cursor="pointer"><Share2 size={18} color="#748C70" /></YStack>}
      />

      <FlatList
        data={filteredProducts}
        renderItem={renderProduct}
        keyExtractor={item => item.id}
        numColumns={columns}
        key={columns}
        ListHeaderComponent={renderStoreHeader}
        ListEmptyComponent={
          <EmptyState
            icon={<Store size={48} color="#858B80" />}
            title={searchQuery ? t('no_results') : t('no_products_in_store')}
          />
        }
        contentContainerStyle={{ padding: GRID_PADDING, paddingTop: 0 }}
        columnWrapperStyle={columns > 1 ? { gap: CARD_GAP, marginBottom: CARD_GAP } : undefined}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => { setRefreshing(true); fetchData().then(() => setRefreshing(false)) }}
          />
        }
      />

      {/* Price range filter sheet */}
      <Sheet open={showFilters} onOpenChange={setShowFilters} noHandle>
        <YStack p="$md" gap="$md">
          <XStack jc="space-between" ai="center">
            <Text preset="heading" fontSize={18}>{t('filters')}</Text>
            <TouchableOpacity onPress={() => setShowFilters(false)}>
              <X size={22} color="#858B80" />
            </TouchableOpacity>
          </XStack>

          {/* Sort */}
          <YStack gap="$sm">
            <Text preset="label" tone="secondary" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('sort_by')}
            </Text>
            <XStack flexWrap="wrap" gap="$sm">
              {(['newest', 'price_low', 'price_high'] as const).map(key => (
                <Chip key={key} variant={sortBy === key ? 'selected' : 'default'} onPress={() => setSortBy(key)}>
                  <ChipText>
                    {key === 'newest' ? t('newest')
                      : key === 'price_low' ? t('price_low_to_high')
                      : t('price_high_to_low')}
                  </ChipText>
                </Chip>
              ))}
            </XStack>
          </YStack>

          {/* Price range */}
          <YStack gap="$xs">
            <Text preset="label" tone="secondary" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('price_range')}
            </Text>
            <XStack gap="$md" ai="center">
              <YStack f={1} gap={4}>
                <Text preset="bodySmall" tone="secondary">Min €</Text>
                <TextInput
                  style={styles.priceInput}
                  value={minPrice}
                  onChangeText={setMinPrice}
                  keyboardType="decimal-pad"
                  placeholder="0"
                  placeholderTextColor="#aaa"
                />
              </YStack>
              <Text preset="body" tone="secondary" mt="$sm">–</Text>
              <YStack f={1} gap={4}>
                <Text preset="bodySmall" tone="secondary">Max €</Text>
                <TextInput
                  style={styles.priceInput}
                  value={maxPrice}
                  onChangeText={setMaxPrice}
                  keyboardType="decimal-pad"
                  placeholder="999"
                  placeholderTextColor="#aaa"
                />
              </YStack>
            </XStack>
          </YStack>

          <XStack gap="$sm" mt="$sm">
            <TouchableOpacity style={styles.resetButton} onPress={resetFilters}>
              <Text style={styles.resetButtonText}>{t('reset')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.applyButton} onPress={() => setShowFilters(false)}>
              <Text style={styles.applyButtonText}>{t('apply')}</Text>
            </TouchableOpacity>
          </XStack>
        </YStack>
      </Sheet>
    </YStack>
  )
}

const styles = StyleSheet.create({
  detailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: theme.colors.brand.primary,
    alignSelf: 'flex-start',
  },
  detailsButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.colors.brand.primary,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 8,
    fontSize: 15,
    color: theme.colors.text.primary,
    outlineStyle: 'none',
  } as any,
  filterIconButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: theme.colors.background.secondary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: theme.colors.ui.border,
  },
  filterIconButtonActive: {
    backgroundColor: theme.colors.brand.primary,
    borderColor: theme.colors.brand.primary,
  },
  priceInput: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    borderWidth: 1,
    borderColor: theme.colors.ui.border,
    color: theme.colors.text.primary,
  },
  resetButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: theme.colors.background.secondary,
    alignItems: 'center',
  },
  resetButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.text.secondary,
  },
  applyButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: theme.colors.brand.primary,
    alignItems: 'center',
  },
  applyButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#fff',
  },
})
